import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React from 'react';
import {SupportPlanTask} from '../../../../model/support/task/support-plan-task';
import {UniversalInput} from '../../shared/UniversalInput';
import {useSupportTaskUpdateDrawerState} from '../state/support-task-update-drawer-state';
import LazyAutocomplete from './LazyAutocomplete';

type Props = {
  organizationId: string
  supportPlanId: string
  isDrawerOpen: boolean
  onDrawerClosed: () => void
  task: SupportPlanTask
  refreshTasks: () => Promise<void>
}

export const SupportTaskUpdateDrawer = ({task, organizationId, supportPlanId, isDrawerOpen, onDrawerClosed, refreshTasks}: Props) => {
  const state = useSupportTaskUpdateDrawerState(task, organizationId, supportPlanId);

  const buildTicket = () => <LazyAutocomplete
    totalCount={state.ticketsData?.total ?? 0}
    pageSize={20}
    fetchOptions={(params) => state.fetchTicketsOptions(params.page, params.search, params.pageSize)}
    value={state.task.ticketId ? ({id: state.task.ticketId, name: state.ticketsData?.items?.find(it => it.id === state.task.ticketId)?.title ?? 'Nie znaleziono'}) : null}
    onChange={(option) => {
      if(option) {
        state.setTask({...state.task, ticketId: option.id, ticketName: option.name})
      } else {
        state.setTask({...state.task, ticketId: null, ticketName: null})
      }
    }}
    label="Wybierz zgłoszenie (opcjonalne)"
  />

  const buildTitle = () => <UniversalInput
    label="Nazwa"
    type="text"
    value={state.task.title}
    onChange={(val) => state.setTask({...state.task, title: val as string})}
  />

  const buildMeetingLink = () => <UniversalInput
    label="Link do spotkania"
    type="text"
    value={state.task.meetingLink}
    onChange={(val) => state.setTask({...state.task, meetingLink: val as string})}
  />

  const buildDescription = () => <UniversalInput
    label="Opis"
    type="textarea"
    value={state.task.description}
    onChange={(val) => state.setTask({...state.task, description: val as string})}
  />

  const buildSummary = () => <UniversalInput
    label="Podsumowanie"
    type="textarea"
    value={state.task.summary}
    onChange={(val) => state.setTask({...state.task, summary: val as string})}
  />

  const buildUserMultiSelect = () => (
    <Autocomplete
      multiple
      options={state.users}
      getOptionLabel={(option) => option.email}
      value={state.task.users || []}
      onChange={(_, selected) => {
        state.setTask({
          ...state.task,
          users: selected,
        });
      }}
      disableCloseOnSelect
      isOptionEqualToValue={(option, value) => option.email === value.email}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.email}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Uczestnicy"
          placeholder="Wybierz uczestników"
        />
      )}
      fullWidth
    />
  );

  const buildTaskTypeOptions = (): string[] => {
    if(task.taskType === 'CONSULTATION') {
      return ['CONSULTATION']
    }

    return ['ANALYSIS', 'LONG_TERM_TASK']
  }

  const buildTaskType = () => <FormControl fullWidth>
    <InputLabel id="demo-simple-select-label">Typ zadania</InputLabel>
    <Select
      labelId="demo-simple-select-label"
      id="demo-simple-select"
      value={state.task.taskType}
      label={'Typ zadania'}
      onChange={event => {
        if(event.target.value !== 'CONSULTATION') {
          state.setTask({...state.task, taskType: event.target.value, users: []})
        } else {
          state.setTask({...state.task, taskType: event.target.value})
        }
      }}
    >
      {
        buildTaskTypeOptions().map(it => <MenuItem value={it}>{state.translate(`support.plan.task.type.${it}`)}</MenuItem>)
      }
    </Select>
  </FormControl>

  const buildStartDate = () => <UniversalInput
    label="Data rozpoczecia pracy"
    type="date"
    value={state.task.startDate}
    onChange={(val) => {
      if(state.task.taskType === 'CONSULTATION') {
        state.setTask({...state.task, startDate: val as string, endDate: val as string})
      } else {
        state.setTask({...state.task, startDate: val as string})
      }
    }}
    min={0}
    step={1}
  />

  const buildEndDate = () => <UniversalInput
    label="Data zakonczenia pracy"
    type="date"
    disabled={state.task.taskType === 'CONSULTATION'}
    value={state.task.endDate}
    onChange={(val) => state.setTask({...state.task, endDate: val as string})}
    min={0}
    step={1}
  />

  const buildStartTime = () => <UniversalInput
    label="Godzina rozpoczecia pracy (opcjonalne)"
    type="time"
    value={state.task.startTime}
    onChange={(val) => state.setTask({...state.task, startTime: val as string})}
    min={0}
    step={1}
  />

  const buildEndTime = () => <UniversalInput
    label="Godzina zakonczenia pracy (opcjonalne)"
    type="time"
    value={state.task.endTime}
    onChange={(val) => state.setTask({...state.task, endTime: val as string})}
    min={0}
    step={1}
  />

  const buildPlannedHours = () =>  <TextField
    label={'Liczba godzin'}
    type="number"
    value={state.task.plannedHours}
    onChange={(e) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value) && value >= 0) {
        state.setTask({ ...state.task, plannedHours: value });
      }
    }}
    fullWidth
    inputProps={{
      min: 0,
      step: 0.5,
    }}
    InputLabelProps={{
      shrink: true,
    }}
  />

  return <Drawer
    anchor="right"
    open={isDrawerOpen}
    onClose={onDrawerClosed}
    PaperProps={{
      sx: {
        width: 480,
        marginTop: '64px',
        height: `calc(100% - 64px)`,
        position: 'fixed',
        right: 0,
        padding: 3,
        overflowY: 'auto',
      }
    }}
  >
    <form onSubmit={async (event) => {
      event.preventDefault();
      await state.handleSubmit()
      await refreshTasks()
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, mt: 0.5 }}>
        <CalendarTodayIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Podgląd zadania
        </Typography>
      </Box>
      <Stack spacing={3}>
        {buildTicket()}
        {buildTitle()}
        {buildDescription()}
        {buildSummary()}
        {buildTaskType()}
        {task.taskType === 'CONSULTATION' ? buildMeetingLink() : <></>}
        {task.taskType === 'CONSULTATION' ? buildUserMultiSelect() : <></>}
        {buildStartDate()}
        {buildEndDate()}
        {buildStartTime()}
        {buildEndTime()}
        {buildPlannedHours()}
        <Button type="submit"
                variant="contained"
                color="primary"
        >
          {state.translate('common.save.title')}
        </Button>
        <Button variant="outlined"
                color="error"
                onClick={async () => {
                  await state.onDeletePressed()
                  await refreshTasks()
                  onDrawerClosed();
                }}
        >
          Usuń
        </Button>
        <Button variant="outlined"
                onClick={onDrawerClosed}
        >
          {state.translate('common.cancel.title')}
        </Button>
      </Stack>
    </form>
  </Drawer>;
};